import { useScopedPathname } from "@-/com.npmjs.expo-router";
import { useSharedValue, withTiming } from "@-/com.npmjs.react-native-reanimated";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { useIsOnboardingActive } from "@-/dev.improve.haniverse.lib.onboarding";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import {
  isLearnMapPathname,
  LEARN_MAP_PATHNAME,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.map.navigator";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { LearnMapCardFrame } from "#learn-map-cards-motion";
import {
  LearnMapModeContext,
  type LearnMapHomeExit,
  type LearnMapMode,
} from "#learn-map-mode-context";
import { LEARN_MAP_DEFAULT_CARDS_PATHNAME } from "#learn-map-default-cards-pathname";
import { LEARN_MAP_TRANSITION_DURATION_MS, learnMapTransition } from "#learn-map-transition";

/**
 * How long a press may lead the route it navigates to, past its own slide. A slide the route
 * never followed is then taken back, so the screen always ends on the mode the route names.
 */
const LEARN_MAP_ROUTE_DEADLINE_MS = LEARN_MAP_TRANSITION_DURATION_MS + 2000;

/** Bound on waiting for an idle moment to prepare the map beneath the cards. */
const LEARN_MAP_PREPARE_IDLE_TIMEOUT_MS = 3000;

/**
 * Derives Learn's mode from Learn's own route: the map is the `/learn/map` route family, a history
 * entry like the deck's and the summary's, and the cards are whatever Learn showed before it.
 * Every change of mode eases `progress`, which every surface that moves reads on the UI thread.
 *
 * The controls that open and close the map go through `switchMode`: the slide starts on the press
 * and the route follows once it is over. A route's commit is heavy (the deck goes idle, the stack
 * pushes), and landing in the middle of the slide it froze the UI thread for frames at a time.
 * A route that changes on its own (a deep link, hardware back) slides when it commits.
 *
 * The map is also Learn's home (`isMapHome`): a cold launch opens Learn on it, and it stays the home
 * until the learner leaves it for the cards.
 */
export function LearnMapModeProvider({ children }: { children: ReactNode }) {
  const pathname = useScopedPathname("/learn");
  // Before the navigation ref is ready, the scoped pathname is `/learn`. Its entry is the map,
  // so initialize the map fully open instead of painting cards and then animating them away.
  const isMapEntry = pathname === "/learn";
  const isMapOpen = isMapEntry || isLearnMapPathname(pathname);
  const isMapShown = isMapEntry || pathname === LEARN_MAP_PATHNAME;
  // Leaving the home for the cards starts the Learn flow, which ends the home for the app session.
  const [hasLearnFlowStartedThisSession] = useContextSelectors(
    NavContext,
    (c) => c.hasLearnFlowStartedThisSession,
  );
  const isOnboardingActive = useIsOnboardingActive();
  const isHomeSession = !hasLearnFlowStartedThisSession && !isOnboardingActive;
  const isMapHome = isMapOpen && isHomeSession;
  const mapHomeExitRef = useRef<LearnMapHomeExit | null>(null);
  const [cardsPathname, setCardsPathname] = useState(() =>
    isMapOpen ? LEARN_MAP_DEFAULT_CARDS_PATHNAME : pathname,
  );
  if (!isMapOpen && cardsPathname !== pathname) {
    setCardsPathname(pathname);
  }
  // The mode a press has started sliding towards, until its route arrives (or its deadline).
  const [pendingIsMapOpen, setPendingIsMapOpen] = useState<boolean | null>(null);
  const pendingIsMapOpenRef = useRef<boolean | null>(null);
  // No card UI is mounted for a map launch. A press to leave mounts it on that same commit;
  // an explicit cards route (including a deep link) requests it too. Once requested, preserve
  // the deck's session and history across later map visits.
  const [areCardsRequested, setAreCardsRequested] = useState(!isMapOpen);
  if (!isMapOpen && !areCardsRequested) {
    setAreCardsRequested(true);
  }
  if (pendingIsMapOpen !== null && pendingIsMapOpen === isMapOpen) {
    setPendingIsMapOpen(null);
  }
  const isHeadingToMap = pendingIsMapOpen ?? isMapOpen;
  const [isMapPrepared, setIsMapPrepared] = useState(isMapOpen);
  const [isMapRequested, setIsMapRequested] = useState(isMapOpen);
  const [isMapPresent, setIsMapPresent] = useState(isMapOpen);
  if (isHeadingToMap && !(isMapPrepared && isMapRequested && isMapPresent)) {
    setIsMapPrepared(true);
    setIsMapRequested(true);
    setIsMapPresent(true);
  }
  const progress = useSharedValue(isMapOpen ? 1 : 0);
  const toolbarProgress = useSharedValue(isMapShown ? 1 : 0);
  const cardFrame = useSharedValue<LearnMapCardFrame | null>(null);
  const sheetTop = useSharedValue(Number.POSITIVE_INFINITY);
  const perchSheetTop = useSharedValue(Number.POSITIVE_INFINITY);
  const wasMapOpen = useRef(isMapOpen);
  const isMapOpenRef = useLatest(isMapOpen);
  const navigationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const switchMode = useCallback(
    (nextIsMapOpen: boolean, navigate: () => void) => {
      const current = pendingIsMapOpenRef.current ?? isMapOpenRef.current;
      if (current === nextIsMapOpen) {
        return;
      }
      pendingIsMapOpenRef.current = nextIsMapOpen;
      if (!nextIsMapOpen) {
        setAreCardsRequested(true);
      }
      setPendingIsMapOpen(nextIsMapOpen);
      progress.set(withTiming(nextIsMapOpen ? 1 : 0, learnMapTransition()));
      toolbarProgress.set(withTiming(nextIsMapOpen ? 1 : 0, learnMapTransition()));
      if (navigationTimer.current !== null) {
        clearTimeout(navigationTimer.current);
      }
      navigationTimer.current = setTimeout(navigate, LEARN_MAP_TRANSITION_DURATION_MS);
    },
    [isMapOpenRef, progress, toolbarProgress],
  );

  useEffect(
    () => () => {
      if (navigationTimer.current !== null) {
        clearTimeout(navigationTimer.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (pendingIsMapOpen === null) {
      return;
    }
    const timer = setTimeout(() => {
      pendingIsMapOpenRef.current = null;
      setPendingIsMapOpen(null);
      const target = isMapOpenRef.current ? 1 : 0;
      progress.set(withTiming(target, learnMapTransition()));
      toolbarProgress.set(withTiming(target, learnMapTransition()));
    }, LEARN_MAP_ROUTE_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [isMapOpenRef, pendingIsMapOpen, progress, toolbarProgress]);

  // Before paint, so the frame that first shows a map route already starts its slide.
  useLayoutEffect(() => {
    const isModeChange = wasMapOpen.current !== isMapOpen;
    wasMapOpen.current = isMapOpen;
    const wasPressed = isModeChange && pendingIsMapOpenRef.current === isMapOpen;
    if (isModeChange) {
      pendingIsMapOpenRef.current = null;
    }
    if (wasPressed) {
      // The press already slid there; the route only confirms it.
      return;
    }
    const toolbarTarget = isMapShown ? 1 : 0;
    toolbarProgress.set(
      isModeChange ? withTiming(toolbarTarget, learnMapTransition()) : toolbarTarget,
    );
    if (isModeChange) {
      progress.set(withTiming(isMapOpen ? 1 : 0, learnMapTransition()));
    }
  }, [isMapOpen, isMapShown, progress, toolbarProgress]);

  useEffect(() => {
    if (isMapPrepared) {
      return;
    }
    // Ready beneath the cards before anyone asks, so opening the map only has to slide.
    const handle = requestIdleCallback(() => setIsMapPrepared(true), {
      timeout: LEARN_MAP_PREPARE_IDLE_TIMEOUT_MS,
    });
    return () => cancelIdleCallback(handle);
  }, [isMapPrepared]);

  useEffect(() => {
    if (isHeadingToMap || !isMapPresent) {
      return;
    }
    // The map slides out on the same clock as the cards slide in; it stops drawing once it is gone.
    const timer = setTimeout(setIsMapPresent, LEARN_MAP_TRANSITION_DURATION_MS, false);
    return () => clearTimeout(timer);
  }, [isHeadingToMap, isMapPresent]);

  const isMapToolbarCurrent = pendingIsMapOpen ?? isMapShown;
  const isMapHomeControl = isMapHome || (isMapToolbarCurrent && isHomeSession);
  const value = useMemo<LearnMapMode>(
    () => ({
      areCardsRequested,
      cardFrame,
      cardsPathname,
      isMapHome,
      isMapHomeControl,
      isMapOpen,
      isMapPrepared,
      isMapPresent,
      isMapRequested,
      isMapShown,
      isMapToolbarCurrent,
      mapHomeExitRef,
      perchSheetTop,
      progress,
      sheetTop,
      switchMode,
      toolbarProgress,
    }),
    [
      areCardsRequested,
      cardFrame,
      cardsPathname,
      isMapHome,
      isMapHomeControl,
      isMapOpen,
      isMapPrepared,
      isMapPresent,
      isMapRequested,
      isMapShown,
      isMapToolbarCurrent,
      progress,
      sheetTop,
      perchSheetTop,
      switchMode,
      toolbarProgress,
    ],
  );
  return <LearnMapModeContext.Provider value={value}>{children}</LearnMapModeContext.Provider>;
}
